import { useMemo } from 'react';
import { useViewportSize } from './useViewportSize';
import { useResponsiveLayout } from './useResponsiveLayout';

interface CanvasSize {
  width: number;
  height: number;
  scale: number;
}

interface UseSquareCanvasOptions {
  maxSize?: number;
  padding?: number;
  reservedTop?: number;
  reservedBottom?: number;
}

export function useSquareCanvas(options: UseSquareCanvasOptions = {}): CanvasSize {
  const {
    maxSize = 600,
    padding = 16,
    reservedTop = 0,
    reservedBottom = 0
  } = options;
  
  const viewport = useViewportSize();
  const { isMobile } = useResponsiveLayout();
  
  const canvasSize = useMemo(() => {
    // Calculate available space
    const availableWidth = viewport.width - (padding * 2);
    const availableHeight = viewport.height - reservedTop - reservedBottom - (padding * 2);
    
    // For mobile, we want to maximize the square within available space
    let size = Math.min(availableWidth, availableHeight);
    
    // Cap at maxSize for larger screens
    if (!isMobile) {
      size = Math.min(size, maxSize);
    }
    
    // Ensure minimum size
    size = Math.max(size, 200);
    
    // Calculate scale factor for high DPI displays
    const scale = window.devicePixelRatio || 1;
    
    return {
      width: Math.floor(size),
      height: Math.floor(size),
      scale
    };
  }, [viewport.width, viewport.height, padding, reservedTop, reservedBottom, maxSize, isMobile]);
  
  return canvasSize;
}